import React, { useEffect, useMemo, useState } from "react";
import { Command } from "cmdk";
import * as Dialog from "@radix-ui/react-dialog";
import { useNavigate } from "react-router-dom";
import { useConnectors } from "@/hooks/use-connectors";
import { useWorkflows } from "@/hooks/use-workflows";
import { useCustomers } from "@/hooks/use-customers";
import { useAuth } from "@/lib/auth";
import { hasMinRole } from "@/lib/rbac";
import { CONNECT_MCP_MIN_ROLE } from "@/lib/guides";
import { Icon } from "@/pages/prototype-icons";
import { globalSearchTestIds } from "@/testids/globalSearchTestIds";
import { searchScore } from "@/lib/search-score";

/**
 * The design's topbar search: a button that opens a command palette on click or
 * Cmd/Ctrl+K.
 *
 * All four groups the design's placeholder names — connectors, workflows,
 * customers, actions — are searched. Customers arrived last, via
 * use-customers.ts; actions after them, carrying the Connect-your-agent row
 * that people hunt for by client name rather than by path; until
 * that hook existed the placeholder deliberately said only "connectors and
 * workflows", because naming a group the palette cannot return is worse than a
 * shorter label. Keep those two in step: if a group is ever dropped, the
 * placeholder drops with it.
 *
 * The button is not decorative. It was the last piece of the topbar missing, and
 * shipping the control without the palette would have put a dead affordance in
 * the centre of every page.
 */
export function GlobalSearch(): React.ReactElement {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Cmd+K on mac, Ctrl+K elsewhere. Ignored while typing so the shortcut cannot
  // swallow a keystroke meant for an input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== "k" || !(e.metaKey || e.ctrlKey)) return;
      const el = document.activeElement as HTMLElement | null;
      const typing = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
      if (typing && !open) return;
      e.preventDefault();
      setOpen((o) => !o);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  /* Every list here is fetched only once the palette is OPENED -- the topbar is on
     every page, and a list query per page load to fill a dialog nobody opened is
     cost with no user.
     Connectors used to be exempt, on the grounds that the pages using them had
     already cached the array. They had not: the Connectors table renders the
     PAGINATED hook, which is a different query key, so nothing warmed this one and
     it fetched the whole 612 kB catalog on every page load for a closed dialog. */
  const { data: connectors = [] } = useConnectors({ enabled: open });
  const { data: workflows = [] } = useWorkflows(undefined, { enabled: open });
  const { data: customers = [] } = useCustomers({ enabled: open });

  const { userContext } = useAuth();
  const canConnectMcp = hasMinRole(userContext?.role ?? "end_user", CONNECT_MCP_MIN_ROLE);

  const go = (to: string) => { setOpen(false); navigate(to); };

  // One row in the palette. `keywords` is optional and matched but never
  // rendered, so a row can answer queries its visible text does not contain.
  interface PaletteItem {
    key: string;
    label: string;
    hint: string;
    keywords?: string[];
    to: string;
  }

  const groups = useMemo<Array<{
    heading: string;
    label: string;
    icon: string;
    items: PaletteItem[];
  }>>(() => [
    {
      // Not a record like the other three, but the thing people come here
      // looking for by name. "Can I use this from Claude?" is asked as a
      // search long before anyone finds the button in the top bar, so the
      // hint carries the client names as keywords: the palette is a strict
      // substring match over label + hint, so typing "cursor", "codex" or
      // "mcp" all land here.
      heading: "Actions",
      label: "Action",
      icon: "plug",
      items: canConnectMcp
        ? [{
            key: "connect-mcp",
            label: "Connect your agent",
            hint: "MCP server",
            // Matched but not displayed. The hint is `shrink-0` in the row
            // below, so a long one squeezes the flex-1 label to nothing and the
            // result renders as a keyword list with no name on it.
            keywords: ["mcp", "claude", "cursor", "codex", "vs code", "gemini", "windsurf", "agent"],
            to: "/settings/mcp",
          }]
        : [],
    },
    {
      heading: "Connectors",
      // Singular, because it labels one result — the group heading scrolls out
      // of view long before the rows under it do.
      label: "Connector",
      icon: "stack",
      items: connectors.map((c) => ({
        key: `connector-${c.id}`,
        label: c.name,
        hint: c.slug,
        to: `/integrations/connectors/${c.slug}`,
      })),
    },
    {
      heading: "Workflows",
      label: "Workflow",
      icon: "workflow",
      items: workflows.map((w) => ({
        key: `workflow-${w.id}`,
        label: w.name,
        hint: w.slug,
        to: `/integrations?tab=workflows&workflow=${w.id}`,
      })),
    },
    {
      heading: "Customers",
      label: "Customer",
      icon: "organization",
      items: customers.map((c) => ({
        key: `customer-${c.id}`,
        label: c.name,
        hint: c.slug,
        // Customers has no detail route of its own; the list page is where a
        // customer is opened from, so the palette lands there rather than on a
        // URL that would 404.
        to: "/settings/customers",
      })),
    },
  ].filter((g) => g.items.length > 0), [canConnectMcp, connectors, workflows, customers]);

  return (
    <>
      <button
        type="button"
        className="topSearch"
        onClick={() => setOpen(true)}
        data-testid={globalSearchTestIds.trigger}
      >
        <Icon name="search" />
        <span>Search connectors, workflows, customers, actions</span>
        <span className="kbd"><i>⌘</i><i>K</i></span>
      </button>

      <Command.Dialog
        open={open}
        onOpenChange={setOpen}
        label="Search"
        shouldFilter
        // cmdk's default fuzzy scorer surfaced results that don't contain the
        // query at all (searching "slack" returned Google Workspace / ManageEngine),
        // so this stays a strict substring match - searchScore returns 0 for
        // anything that does not contain the query, and cmdk hides those.
        //
        // What it adds is a ranking. The old filter returned 1 for every match,
        // which left cmdk's sort with nothing to order by, so results came back
        // in the order the groups are declared: searching "test" listed every
        // connector whose slug merely contains those letters, and the customer
        // named "test" came last.
        //
        // Name and slug are read from `keywords` rather than split out of
        // `value`: a name can contain spaces, so splitting would be guesswork.
        // `value` stays the joined string because cmdk uses it as the item's
        // identity and it has to stay unique - a connector and a customer can
        // both be called "test", which is exactly the case in the report.
        filter={(value, search, keywords) =>
          searchScore(keywords?.[0] ?? value, keywords?.[1] ?? "", search)
        }
        className="fixed left-1/2 top-[18vh] z-[2000] w-[560px] max-w-[92vw] -translate-x-1/2 overflow-hidden rounded-xl border border-border bg-card shadow-2xl" // design-tokens-ignore: overlay exception
        overlayClassName="fixed inset-0 z-[1999] bg-black/50"
        data-testid={globalSearchTestIds.dialog}
      >
        {/* Radix checks for a real element carrying its own titleId --
            `document.getElementById(titleId)` in react-dialog's TitleWarning --
            so cmdk's `label` prop, which only sets aria-label, never satisfied it
            and every open logged a console error. sr-only rather than removed:
            the palette has no visible heading by design, and a hidden title is
            what Radix's own message recommends. */}
        <Dialog.Title className="sr-only">Search</Dialog.Title>
        <div className="flex items-center gap-2 border-b border-border px-4">
          <span className="shrink-0 text-fg-muted"><Icon name="search" /></span>
          <Command.Input
            autoFocus
            placeholder="Search connectors, workflows, customers, actions"
            className="h-12 flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
            data-testid={globalSearchTestIds.input}
          />
        </div>
        <Command.List className="max-h-[52vh] overflow-y-auto p-2">
          <Command.Empty className="px-3 py-6 text-center text-sm text-muted-foreground">
            Nothing matches that.
          </Command.Empty>
          {groups.map((g) => (
            <Command.Group
              key={g.heading}
              heading={g.heading}
              className="px-1 py-1 text-[12px] font-semibold uppercase tracking-wider text-muted-foreground"
            >
              {g.items.map((it) => (
                <Command.Item
                  key={it.key}
                  value={[it.label, it.hint, ...(it.keywords ?? [])].join(" ")}
                  // Exactly two entries: the filter below reads keywords[0] as
                  // the name and keywords[1] as the hint, so anything at index
                  // 2 or beyond is silently never matched. Extra keywords are
                  // appended to the hint slot instead, which is also why they
                  // score as a hint match rather than a name one.
                  keywords={[it.label, [it.hint, ...(it.keywords ?? [])].join(" ")]}
                  onSelect={() => go(it.to)}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground data-[selected=true]:bg-accent"
                  data-testid={globalSearchTestIds.item(it.key)}
                >
                  <Icon name={g.icon} />
                  <span className="min-w-0 flex-1 truncate normal-case">{it.label}</span>
                  {/* A palette that mixes three resources in one scrolling list
                      has to say which one each row is: the group heading above
                      it is gone the moment the list scrolls, and a connector
                      and a workflow of the same name are otherwise identical
                      rows. */}
                  <span
                    className="shrink-0 rounded-pill border border-border px-2 py-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
                    data-testid={globalSearchTestIds.itemCategory(it.key)}
                  >
                    {g.label}
                  </span>
                  <span className="shrink-0 truncate text-xs normal-case text-muted-foreground">{it.hint}</span>
                </Command.Item>
              ))}
            </Command.Group>
          ))}
        </Command.List>
      </Command.Dialog>
    </>
  );
}
